import { Link } from 'react-router-dom';
import { CheckIcon, ChevronDownIcon, FileTextIcon, MailIcon, PrinterIcon, RepeatIcon, UserIcon } from 'lucide-react';
import { toast } from 'sonner';
import { printPackingSlip } from './packingSlip';
import { ProductImage } from '../../ui/ProductImage';
import { Badge } from '../ui/Badge';
import { useAdmin } from '../AdminContext';
import { btnPrimary, btnSecondary } from '../ui/styles';
import { euro } from '../../core/format/currency';
import { inboxOrderTotal, isPaid, nextStatus, paymentLabel, received, statusLabel, statusTone } from './inboxRules';
import type { InboxOrder } from '../../core/types/adminRequests';
import type { Tracking } from '../../core/types/account';
import { trackingUrl } from '../../core/rules/tracking';
import { formatStreet } from '../../core/rules/contact';
import { isPhysical } from '../../core/rules/stock';
import { requestBundleOf } from '../../core/rules/orderBundles';
import { ShipForm } from './ShipForm';
type Props = {
  order: InboxOrder;
  open: boolean;
  onToggle: () => void;
  onAdvance: (tracking?: Tracking) => void;
};

/** Eén bestelling: dicht een regel, open de inhoud en de volgende stap. */
export function InboxOrderRow({
  order: o,
  open,
  onToggle,
  onAdvance
}: Props) {
  const {
    index,
    site
  } = useAdmin();
  const sum = inboxOrderTotal(o);
  // Het cadeau komt van de server, vastgelegd bij het plaatsen: nooit opnieuw rekenen met de prijzen van nu.
  const gift = o.gift;
  const paid = isPaid(o);
  // Niet betaald = niet inpakken. De volgende stap verschijnt pas na de betaling.
  const next = paid ? nextStatus[o.status] : undefined;
  const panelId = `order-${o.id}`;
  const addr = o.order.address;
  // Alleen cadeaubonnen of een kuur: er gaat niets op de post (geen pakbon, geen verzendadres).
  const nothingToShip = o.order.lines.every(l => {
    const p = index.productById(l.productId);
    return p !== undefined && !isPhysical(p);
  });
  const slip = async () => {
    const ok = await printPackingSlip(o, index.productById, site.salon, gift, id => index.routineById(id)?.name);
    if (!ok) toast.error('Pakbon afdrukken lukte niet', {
      description: 'Probeer het opnieuw of gebruik een andere browser.'
    });
  };
  return <li data-id="element-1730" data-mp="3qnjxm1ln8kk">
      <button type="button" onClick={onToggle} aria-expanded={open} aria-controls={panelId} className="flex w-full flex-wrap items-center gap-x-5 gap-y-1 px-5 py-4 text-left transition-colors duration-150 ease-soft hover:bg-shell" data-id="element-1731" data-mp="3rgnyf96bjz0">
        {paid && o.kuurToLink ? <Badge tone="accent" data-id="element-1732" data-mp="329lzlbc6q7k6">Kuur koppelen</Badge> : <Badge tone={paid ? statusTone[o.status] : 'neutral'} data-id="element-1733" data-mp="3gx73h7gvwat5">{paid ? statusLabel[o.status] : paymentLabel[o.payment.status]}</Badge>}
        <span className="min-w-0 flex-1" data-id="element-1734" data-mp="3c5yvmfv7cxi3">
          <span className="block text-[14px] text-ink" data-id="element-1735" data-mp="2oh65x156blvx">{o.name}</span>
          <span className="block text-[12px] text-muted" data-id="element-1736" data-mp="2ofbjtji61nq9">
            {o.id} · {received(o.receivedAt)} · {o.city}
          </span>
        </span>
        {o.order.skinNote && <>
            <span className="h-2 w-2 shrink-0 rounded-full bg-clay" aria-hidden="true" data-id="element-1737" data-mp="yyfvre6h4pts" />
            <span className="sr-only" data-id="element-1738" data-mp="13ris1u2myddo">Met notitie</span>
          </>}
        <span className="text-[14px] tabular-nums text-ink" data-id="element-1739" data-mp="p2mfpktjqe79">{euro(sum, {
          cents: true
        })}</span>
        <ChevronDownIcon className={`h-4 w-4 shrink-0 text-muted transition-transform duration-150 ease-soft ${open ? 'rotate-180' : ''}`} strokeWidth={1.5} aria-hidden="true" data-id="element-1740" data-mp="u063pcg1q5ce" />
      </button>
      {open && <div id={panelId} className="grid gap-6 border-t border-line bg-shell px-5 py-5 md:grid-cols-2" data-id="element-1741" data-mp="1mniphkedu94n">
          <div data-id="element-1742" data-mp="18rww7114936f">
            <ul className="space-y-2" data-id="element-1743" data-mp="1crfc1ie2yo2e">
              {o.order.lines.map(l => {
            const p = index.productById(l.productId);
            return <li key={l.productId} className="flex items-center gap-3 text-[14px]" data-id="element-1744" data-mp="3termpbfoqk0f">
                    {p && <ProductImage src={p.image} className="h-10 w-10 shrink-0 rounded p-0.5" data-id="element-1745" data-mp="1dlbyavnrr8b" />}
                    <span className="min-w-0 flex-1" data-id="element-1746" data-mp="hkpujg2gb7k6">
                      <span className="block truncate text-ink" data-id="element-1747" data-mp="cs6gkhzuqaeu">
                        {l.qty} × {p?.name ?? l.productId}
                      </span>
                      {requestBundleOf(l) && <span className="block text-[12px] text-muted" data-id="element-1748" data-mp="2v0ob6fi4dd1">Als set: {index.routineById(requestBundleOf(l) ?? '')?.name ?? 'protocol'}</span>}
                      {l.subscribe && <span className="flex items-center gap-1 text-[12px] text-clay" data-id="element-1749" data-mp="vk7pkd6cbh1z">
                          <RepeatIcon className="h-3 w-3" strokeWidth={1.75} aria-hidden="true" data-id="element-1750" data-mp="qjbijqqozesc" />
                          Automatisch bijvullen
                        </span>}
                    </span>
                  </li>;
          })}
            </ul>
            {gift && <p className="mt-4 text-[13px] text-muted" data-id="element-1751" data-mp="377xgddxm2yi8">
                Cadeau erbij: <span className="text-ink" data-id="element-1752" data-mp="1mrhbwhwso4c6">{gift}</span>
              </p>}
            {paid && o.kuurToLink && <p className="mt-4 rounded-md bg-blush/50 px-3 py-2 text-[13px] text-ink" data-id="element-1753" data-mp="1qst48v71z3o2">
                {o.customerId ? 'Deze kuur kon niet automatisch op haar account: ze heeft al een andere lopende kuur. Leg hem vast in het dossier (Kuur › Vooruit betaald vastleggen); daarna verdwijnt deze melding.' : 'Gastbestelling: de kuur staat nog op geen account. Vraag haar een account te maken met dit mailadres, en leg de kuur dan vast in haar dossier.'}
              </p>}
            {!paid && <p className="mt-4 rounded-md bg-sand px-3 py-2 text-[13px] text-ink" data-id="element-1754" data-mp="2jgynfdigkkls">
                {o.payment.status === 'open' ? 'De klant rondt de betaling nog af. Pak hem pas in als hij op Betaald staat.' : 'Niet betaald. Niet inpakken; de klant kan opnieuw bestellen.'}
              </p>}
          </div>
          <div className="space-y-3 text-[13px]" data-id="element-1755" data-mp="1sed7sloqy257">
            {nothingToShip ? <p className="text-muted" data-id="element-1756" data-mp="1ng1zwgi0i1pm">Niets te versturen: cadeaubon per mail, kuur in Mijn omgeving.</p> : <address className="not-italic" data-id="element-1757" data-mp="21fta065eqxyc">
                <span className="block text-[12px] text-muted" data-id="element-1758" data-mp="1wex5bqd8f7rv">Verzenden naar</span>
                <span className="block text-[14px] leading-snug text-ink" data-id="element-1759" data-mp="2bcfswpe6bexq">
                  {`${addr.firstName} ${addr.lastName}`.trim() || o.name}
                  <br data-id="element-1760" data-mp="26d37m92yafx9" />
                  {formatStreet(addr)}
                  <br data-id="element-1761" data-mp="2k5j0ih250xkg" />
                  {addr.postcode} {addr.city}
                  {addr.country === 'BE' && ' · België'}
                </span>
              </address>}
            {o.order.skinNote && <p className="rounded-md bg-blush/50 px-3 py-2 text-ink" data-id="element-1762" data-mp="2u48j6c1uym1r">
                <span className="block text-[12px] text-muted" data-id="element-1763" data-mp="2p4m8a99i5hw4">Notitie voor jou</span>“{o.order.skinNote}”
              </p>}
            <p className="text-muted" data-id="element-1764" data-mp="38r77vz9bz2cs">
              {o.order.email}
              {o.order.code && <>
                  {' '}
                  · code <span className="text-ink" data-id="element-1765" data-mp="2yryi7ili09w8">{o.order.code}</span>
                </>}
              {o.order.refillReminder && ' · wil een seintje als het bijna op is'}
              {!o.customerId && ' · gastbestelling'}
            </p>
            {o.status === 'verzonden' && <p className="text-muted" data-id="element-1766" data-mp="2upuw8017dmwp">
                {o.tracking ? <>
                    {o.tracking.carrier} ·{' '}
                    <a href={trackingUrl(o.tracking)} target="_blank" rel="noopener noreferrer" className="text-ink underline underline-offset-2" data-id="element-1767" data-mp="2g2ryptcvre9d">
                      {o.tracking.code}
                      <span className="sr-only" data-id="element-1768" data-mp="271trz3b24sn3"> (opent in een nieuw tabblad)</span>
                    </a>
                  </> : 'Verzonden zonder track & trace'}
              </p>}
            {next === 'verzonden' && !nothingToShip && <ShipForm orderId={o.id} onShip={onAdvance} data-id="element-1769" data-mp="2mjlczaw2ujdt" />}
            <div className="grid grid-cols-2 gap-2 pt-1 sm:flex sm:flex-wrap" data-id="element-1770" data-mp="2hjgtrd3sbajw">
              {next && next !== 'verzonden' && !nothingToShip && <button type="button" onClick={() => onAdvance()} className={btnPrimary} data-id="element-1771" data-mp="2cjkozbn9jf3a">
                  <CheckIcon className="h-4 w-4" strokeWidth={2} aria-hidden="true" data-id="element-1772" data-mp="28ju2hk4rrl4k" />
                  {statusLabel[next]}
                </button>}
              {o.status !== 'verzonden' && paid && !nothingToShip && <button type="button" onClick={() => void slip()} className={btnSecondary} data-id="element-1773" data-mp="1yqv2l63to85n">
                  <PrinterIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-1774" data-mp="1tvph28ec0vi3" />
                  Pakbon
                </button>}
              <Link to={`/be/bestellingen/${o.id}`} state={{
            back: {
              to: '/be/inbox',
              label: 'Inbox'
            }
          }} className={btnSecondary} data-id="element-1775" data-mp="1ksy3o31wbpnt">
                <FileTextIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-1776" data-mp="am5uabtyppi" />
                Openen
              </Link>
              {o.customerId ? <Link to={`/be/klanten/${o.customerId}`} className={btnSecondary} data-id="element-1777" data-mp="9bkcz1hkfdcb">
                  <UserIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-1778" data-mp="eaoldpwo171l" />
                  Dossier
                </Link> : <a href={`mailto:${o.order.email}`} className={btnSecondary} data-id="element-1779" data-mp="3cudmn3783i17">
                  <MailIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-1780" data-mp="3hsf81us4ftfu" />
                  Mail klant
                </a>}
            </div>
          </div>
        </div>}
    </li>;
}